import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  Star, 
  ChevronLeft, 
  ChevronRight,
  Award,
  Users,
  Clock,
  Shield,
  Hammer,
  Paintbrush,
  LayoutGrid,
  Layers,
  Sparkles,
  Home as HomeIcon,
  Phone,
  MapPin,
  Zap,
  ZoomIn,
  Play,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ImageLightbox, { GalleryImage } from "@/components/ImageLightbox";
import LazyImage from "@/components/LazyImage";
import { useLanguage } from "@/i18n/LanguageContext";
import useCountUp from "@/hooks/useCountUp";

// Static data - no backend required
const STATIC_PROJECTS = [
  {
    id: 1,
    title: "Rénovation Appartement Bruxelles",
    description: "Transformation complète d'un appartement de 120m² avec cuisine ouverte et finitions haut de gamme.",
    location: "Bruxelles",
    category: "Rénovation Complète",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
    after_image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800"
  },
  {
    id: 2,
    title: "Salle de Bain Moderne",
    description: "Création d'une salle de bain contemporaine avec douche à l'italienne.",
    location: "Waterloo",
    category: "Salle de Bain",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800",
    after_image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800"
  },
  {
    id: 3,
    title: "Carrelage Grand Format",
    description: "Pose de carrelage 80x80 dans salon et cuisine ouverte, finitions soignées.",
    location: "Uccle",
    category: "Carrelage",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800",
    after_image: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800"
  },
  {
    id: 4,
    title: "Peinture & Décoration",
    description: "Rafraîchissement complet avec peinture décorative et finitions soignées.",
    location: "Anderlecht",
    category: "Peinture",
    year: "2024",
    before_image: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=800",
    after_image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800"
  }
];

const STATIC_TESTIMONIALS = [
  {
    id: 1,
    name: "Marie Dubois",
    text: "Excellent travail ! L'équipe a été professionnelle du début à la fin. Notre appartement a été complètement transformé. Je recommande vivement Perfect Team Renov.",
    rating: 5,
    location: "Bruxelles",
    project_type: "Rénovation Complète"
  },
  {
    id: 2,
    name: "Jean-Pierre Martin",
    text: "Très satisfait de la rénovation de notre salle de bain. Travail soigné, délais respectés et prix correct. Une équipe à l'écoute de nos besoins.",
    rating: 5,
    location: "Waterloo",
    project_type: "Salle de Bain"
  },
  {
    id: 3,
    name: "Sophie Lambert",
    text: "Nous avons fait appel à Perfect Team Renov pour la peinture de notre maison. Résultat impeccable, finitions parfaites. Merci à toute l'équipe !",
    rating: 5,
    location: "Uccle",
    project_type: "Peinture"
  },
  {
    id: 4,
    name: "Pierre Henrard",
    text: "Professionnalisme et qualité au rendez-vous. Les travaux de plafonnage ont été réalisés avec soin. Je recommande sans hésitation.",
    rating: 5,
    location: "Anderlecht",
    project_type: "Plafonnage"
  }
];

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = "", label, icon: Icon }) => {
  const { count, elementRef } = useCountUp(end, 2500, true);
  
  return (
    <div ref={elementRef} className="text-center group">
      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#B87333] to-[#CD7F32] rounded-2xl flex items-center justify-center shadow-lg shadow-[#B87333]/20 group-hover:scale-110 transition-transform">
        <Icon className="text-white" size={28} />
      </div>
      <div className="text-5xl md:text-6xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] counter-number">
        {count}{suffix}
      </div>
      <div className="text-[#6B6B6B] text-sm uppercase tracking-widest mt-2">
        {label}
      </div>
    </div>
  );
};

const HomePage = () => {
  const { t } = useLanguage();
  const [projects] = useState(STATIC_PROJECTS);
  const [testimonials] = useState(STATIC_TESTIMONIALS);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [activeService, setActiveService] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) =>
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const services = [
    {
      icon: HomeIcon,
      key: "renovation",
      image: "https://images.unsplash.com/photo-1760072513367-55182245e76c?w=800",
    },
    {
      icon: Hammer,
      key: "plastering",
      image: "https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=800",
    },
    {
      icon: Paintbrush,
      key: "painting",
      image: "https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?w=800",
    },
    {
      icon: LayoutGrid,
      key: "drywall",
      image: "https://images.unsplash.com/photo-1758448500631-644bb3c1c942?w=800",
    },
    {
      icon: Layers,
      key: "flooring",
      image: "https://images.unsplash.com/photo-1771862956369-c840b0a1fc9d?w=800",
    },
    {
      icon: Sparkles,
      key: "bathroom",
      image: "https://images.unsplash.com/photo-1771239048293-72abf673adb2?w=800",
    },
  ];

  const galleryImages = [
    "https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?w=600",
    "https://images.unsplash.com/photo-1760072513367-55182245e76c?w=600",
    "https://images.unsplash.com/photo-1771239048293-72abf673adb2?w=600",
    "https://images.unsplash.com/photo-1758448500631-644bb3c1c942?w=600",
    "https://images.unsplash.com/photo-1771862956369-c840b0a1fc9d?w=600",
    "https://images.unsplash.com/photo-1769970502055-6d1de307406d?w=600",
    "https://images.unsplash.com/photo-1758998202918-d921125a700f?w=600",
    "https://images.unsplash.com/photo-1758548157466-7c454382035a?w=600",
  ];

  const whyUsKeys = ["quality", "deadlines", "transparent", "team", "fast"];
  const whyUsIcons = [Award, Clock, Shield, Users, Zap];

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section - Dark with Ken Burns */}
      <section className="relative min-h-screen overflow-hidden bg-[#0A0A0A]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1760072513367-55182245e76c?w=1920"
            alt="Luxury interior renovation"
            className="w-full h-full object-cover animate-kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-40 right-20 w-96 h-96 bg-[#B87333]/10 rounded-full blur-3xl animate-parallax" />
        <div className="absolute bottom-40 left-10 w-64 h-64 bg-[#B87333]/5 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full py-32">
            <div>
              <div className="inline-flex items-center space-x-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5 mb-8 animate-slide-up">
                <span className="w-2 h-2 bg-[#B87333] rounded-full animate-pulse" />
                <span className="text-[#B87333] font-medium text-sm uppercase tracking-widest">
                  Expert en rénovation depuis 15 ans
                </span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-['Cormorant_Garamond'] font-bold text-white leading-[1.1] mb-6 animate-slide-up stagger-1">
                Rénovation intérieure
                <span className="block text-copper-gradient">à Bruxelles</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-4 leading-relaxed max-w-xl animate-slide-up stagger-2 font-medium">
                Travail soigné – Chantier propre – Délais respectés
              </p>
              
              <p className="text-lg text-white/60 mb-8 leading-relaxed max-w-xl animate-slide-up stagger-2">
                Plafonnage • Peinture • Salle de bain • Carrelage • Pose de sol
              </p>
              
              <div className="flex items-center space-x-4 text-white/50 mb-10 animate-slide-up stagger-3">
                <MapPin size={18} className="text-[#B87333]" />
                <span className="text-sm tracking-wider">Bruxelles et alentours</span>
              </div>

              {/* Triple CTA */}
              <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-4" style={{ position: 'relative', zIndex: 20 }}>
                <Link to="/devis">
                  <Button
                    data-testid="hero-quote-btn"
                    className="bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-8 py-7 text-lg rounded-full shadow-xl shadow-[#B87333]/30 hover:shadow-[#B87333]/50 transition-all cta-pulse"
                  >
                    Devis Gratuit 24h
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <a 
                  href={`https://wa.me/32488467522?text=${encodeURIComponent("Bonjour Perfect Team Renov 👋\n\nJe visite votre site et je suis intéressé(e) par vos services de rénovation à Bruxelles.\n\nPouvez-vous me recontacter pour discuter de mon projet ?\n\nMerci !")}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="hero-whatsapp-btn"
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-7 text-lg rounded-full shadow-xl"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    WhatsApp
                  </Button>
                </a>
                <a href="tel:+32488467522">
                  <Button
                    data-testid="hero-call-btn"
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] px-8 py-7 text-lg rounded-full backdrop-blur-sm"
                  >
                    <Phone className="mr-2" size={20} />
                    Appelez-nous
                  </Button>
                </a>
              </div>
            </div>

            {/* Hero right side - Featured image with overlay card */}
            <div className="hidden lg:block relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <LazyImage
                  src="https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?w=700"
                  alt="Modern interior"
                  className="w-full h-[500px] object-cover"
                  wrapperClassName="w-full h-[500px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#B87333] to-[#CD7F32] text-white p-8 rounded-2xl shadow-2xl float-badge">
                <div className="text-5xl font-['Cormorant_Garamond'] font-bold">15+</div>
                <div className="text-sm opacity-90 uppercase tracking-wider">{t("about.experience_badge")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#B87333] rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-8 bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Award className="text-[#B87333] mb-2" size={28} />
              <span className="text-white font-semibold">15+ ans</span>
              <span className="text-white/50 text-sm">d'expérience</span>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle2 className="text-[#B87333] mb-2" size={28} />
              <span className="text-white font-semibold">500+</span>
              <span className="text-white/50 text-sm">projets réalisés</span>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="text-[#B87333] mb-2" size={28} />
              <span className="text-white font-semibold">Garantie</span>
              <span className="text-white/50 text-sm">satisfaction</span>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="text-[#B87333] mb-2" size={28} />
              <span className="text-white font-semibold">Délais</span>
              <span className="text-white/50 text-sm">respectés</span>
            </div>
            <div className="flex flex-col items-center col-span-2 md:col-span-1">
              <Zap className="text-[#B87333] mb-2" size={28} />
              <span className="text-white font-semibold">Devis gratuit</span>
              <span className="text-white/50 text-sm">sans engagement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Light Background */}
      <section className="py-24 bg-gradient-to-b from-[#FAF9F7] to-white relative overflow-hidden" data-testid="stats-section">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#B87333]/30 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              {t("about.badge")}
            </span>
            <h2 className="text-4xl sm:text-5xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
              L'Excellence en <span className="text-copper-gradient">Chiffres</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <AnimatedCounter end={500} suffix="+" label={t("stats.projects")} icon={Award} />
            <AnimatedCounter end={15} label={t("stats.experience")} icon={Clock} />
            <AnimatedCounter end={100} suffix="%" label={t("stats.satisfaction")} icon={Users} />
            <AnimatedCounter end={3} label={t("stats.regions")} icon={MapPin} />
          </div>
        </div>
      </section>

      {/* About Section - White/Light */}
      <section className="py-28 bg-white relative overflow-hidden" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-8">
                {t("about.badge")}
              </span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] mb-8 leading-[1.1]">
                {t("about.title1")}<br />
                <span className="text-copper-gradient">{t("about.title2")}</span>
              </h2>
              
              <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">
                {t("about.description1")}
              </p>
              
              <p className="text-[#6B6B6B] mb-10 leading-relaxed">
                {t("about.description2")}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-10">
                {t("about.features").map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="text-[#B87333] flex-shrink-0" size={18} />
                    <span className="text-[#4A4A4A] text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <Button className="bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] px-6 rounded-full">
                    {t("about.cta_services")}
                  </Button>
                </Link>
                <Link to="/projets">
                  <Button variant="outline" className="border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white rounded-full">
                    {t("about.cta_projects")}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-5">
                  <div className="rounded-2xl overflow-hidden shadow-lg gallery-item cursor-pointer" onClick={() => openLightbox(0)}>
                    <LazyImage
                      src="https://images.unsplash.com/photo-1771239048293-72abf673adb2?w=400"
                      alt="Bathroom"
                      className="w-full h-52 object-cover"
                      wrapperClassName="w-full h-52"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg gallery-item cursor-pointer" onClick={() => openLightbox(1)}>
                    <LazyImage
                      src="https://images.unsplash.com/photo-1771862956369-c840b0a1fc9d?w=400"
                      alt="Kitchen"
                      className="w-full h-72 object-cover"
                      wrapperClassName="w-full h-72"
                    />
                  </div>
                </div>
                <div className="space-y-5 pt-10">
                  <div className="rounded-2xl overflow-hidden shadow-lg gallery-item cursor-pointer" onClick={() => openLightbox(2)}>
                    <LazyImage
                      src="https://images.unsplash.com/photo-1760072513367-55182245e76c?w=400"
                      alt="Living room"
                      className="w-full h-72 object-cover"
                      wrapperClassName="w-full h-72"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg gallery-item cursor-pointer" onClick={() => openLightbox(3)}>
                    <LazyImage
                      src="https://images.unsplash.com/photo-1758448500631-644bb3c1c942?w=400"
                      alt="Interior"
                      className="w-full h-52 object-cover"
                      wrapperClassName="w-full h-52"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Dark */}
      <section className="py-28 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-8">
              {t("services.badge")}
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
              {t("services.title1")} <span className="text-copper-gradient">{t("services.title2")}</span>
            </h2>
            <p className="text-lg text-white/60">
              {t("services.description")}
            </p>
          </div>

          {/* Interactive Service Showcase */}
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div className="relative h-[550px] rounded-3xl overflow-hidden shadow-2xl">
              <LazyImage
                src={services[activeService].image}
                alt={t(`services.items.${services[activeService].key}.title`)}
                className="w-full h-full object-cover transition-all duration-700"
                wrapperClassName="w-full h-full"
                showSpinner={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <div className="flex items-center space-x-4 mb-4">
                  {(() => {
                    const Icon = services[activeService].icon;
                    return <Icon size={32} className="text-[#B87333]" />;
                  })()}
                  <h3 className="text-3xl font-['Cormorant_Garamond'] font-bold text-white">
                    {t(`services.items.${services[activeService].key}.title`)}
                  </h3>
                </div>
                <p className="text-white/70 text-lg">
                  {t(`services.items.${services[activeService].key}.description`)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`text-left p-6 rounded-2xl transition-all duration-300 ${
                    activeService === index
                      ? "bg-gradient-to-br from-[#B87333] to-[#CD7F32] text-white shadow-xl shadow-[#B87333]/20"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                  }`}
                >
                  <service.icon
                    size={28}
                    className={activeService === index ? "text-white" : "text-[#B87333]"}
                  />
                  <h4 className="font-semibold mt-4 mb-2">
                    {t(`services.items.${service.key}.title`)}
                  </h4>
                  <p className={`text-sm ${activeService === index ? "text-white/80" : "text-white/50"}`}>
                    {t(`services.items.${service.key}.description`)}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button
                data-testid="all-services-btn"
                className="bg-white text-[#0A0A0A] hover:bg-white/90 px-8 py-6 rounded-full"
              >
                {t("services.cta_all")}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Before/After Section - Light */}
      <section className="py-28 bg-gradient-to-b from-[#FAF9F7] to-white" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-20">
            <div>
              <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-8">
                {t("beforeAfter.badge")}
              </span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] mb-8">
                {t("beforeAfter.title1")} <span className="text-copper-gradient">/</span> {t("beforeAfter.title2")}
              </h2>
              <p className="text-lg text-[#6B6B6B] mb-8 leading-relaxed">
                {t("beforeAfter.description")}
              </p>
              <Link to="/projets">
                <Button className="bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] px-6 rounded-full">
                  {t("beforeAfter.cta")}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            
            {projects.length > 0 && (
              <div className="space-y-6">
                <div className="before-after-container">
                  <BeforeAfterSlider
                    beforeImage={projects[0]?.before_image}
                    afterImage={projects[0]?.after_image}
                    beforeLabel={t("beforeAfter.before")}
                    afterLabel={t("beforeAfter.after")}
                  />
                </div>
                <div className="text-center">
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#0A0A0A]">
                    {projects[0]?.title}
                  </h3>
                  <p className="text-[#6B6B6B]">{projects[0]?.location}</p>
                </div>
              </div>
            )}
          </div>

          {/* More transformations */}
          <div className="grid md:grid-cols-3 gap-8">
            {projects.slice(1, 4).map((project) => (
              <div key={project.id} className="group">
                <div className="rounded-2xl overflow-hidden shadow-lg card-modern">
                  <BeforeAfterSlider
                    beforeImage={project.before_image}
                    afterImage={project.after_image}
                    beforeLabel={t("beforeAfter.before")}
                    afterLabel={t("beforeAfter.after")}
                  />
                </div>
                <div className="mt-5 text-center">
                  <h4 className="font-semibold text-[#0A0A0A]">{project.title}</h4>
                  <p className="text-sm text-[#6B6B6B]">{project.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Dark */}
      <section className="py-28 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#B87333]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#B87333]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-8">
              {t("whyUs.badge")}
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
              {t("whyUs.title1")} <span className="text-copper-gradient">{t("whyUs.title2")}</span>
            </h2>
            <p className="text-lg text-white/60">
              {t("whyUs.description")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {whyUsKeys.map((key, index) => {
              const Icon = whyUsIcons[index];
              return (
                <div
                  key={key}
                  className="glass-dark rounded-2xl p-6 hover:border-[#B87333]/30 transition-all group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#B87333] to-[#CD7F32] rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="text-white" size={26} />
                  </div>
                  <h3 className="text-lg font-['Cormorant_Garamond'] font-semibold text-white mb-2">
                    {t(`whyUs.items.${key}.title`)}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {t(`whyUs.items.${key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section - Light */}
      <section className="py-28 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              Portfolio
            </span>
            <h2 className="text-4xl sm:text-5xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] mb-6">
              Nos <span className="text-copper-gradient">Réalisations</span>
            </h2>
            <p className="text-lg text-[#6B6B6B]">
              Découvrez notre savoir-faire à travers nos projets de rénovation les plus récents.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                className={`relative rounded-2xl overflow-hidden cursor-pointer gallery-item ${
                  index === 0 ? 'col-span-2 row-span-2' : ''
                }`}
                onClick={() => openLightbox(index)}
              >
                <LazyImage
                  src={img}
                  alt={`Réalisation ${index + 1}`}
                  className={`w-full object-cover ${index === 0 ? 'h-full min-h-[400px]' : 'h-64'}`}
                  wrapperClassName={`w-full ${index === 0 ? 'h-full min-h-[400px]' : 'h-64'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-[#B87333] p-4 rounded-full transform scale-0 hover:scale-100 transition-transform">
                    <ZoomIn className="text-white" size={24} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/projets">
              <Button className="bg-[#0A0A0A] text-white hover:bg-[#1A1A1A] px-8 py-6 rounded-full">
                Voir Tous les Projets
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Dark */}
      <section className="py-28 bg-[#0A0A0A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-8">
              {t("testimonials.badge")}
            </span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
              {t("testimonials.title1")} <span className="text-copper-gradient">{t("testimonials.title2")}</span>
            </h2>
            <p className="text-lg text-white/60">
              {t("testimonials.description")}
            </p>
          </div>

          {testimonials.length > 0 && (
            <div className="relative max-w-4xl mx-auto">
              <div className="glass-dark rounded-3xl p-12 md:p-16">
                <div className="flex justify-center mb-8">
                  {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                    <Star
                      key={i}
                      className="text-[#B87333] fill-current mx-1"
                      size={28}
                    />
                  ))}
                </div>
                <p className="text-white text-xl md:text-2xl text-center mb-10 leading-relaxed italic font-['Cormorant_Garamond']">
                  "{testimonials[currentTestimonial]?.text}"
                </p>
                <div className="text-center">
                  <p className="text-white text-xl font-semibold">
                    {testimonials[currentTestimonial]?.name}
                  </p>
                  <p className="text-[#B87333]">
                    {testimonials[currentTestimonial]?.location} • {testimonials[currentTestimonial]?.project_type}
                  </p>
                </div>
              </div>

              <div className="flex justify-center items-center mt-10 space-x-8">
                <button
                  onClick={prevTestimonial}
                  data-testid="prev-testimonial-btn"
                  className="p-4 bg-white/5 border border-white/10 rounded-full hover:bg-[#B87333] hover:border-[#B87333] transition-all"
                >
                  <ChevronLeft className="text-white" size={24} />
                </button>
                <div className="flex space-x-3">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === currentTestimonial
                          ? "bg-[#B87333] w-10"
                          : "bg-white/20 w-2 hover:bg-white/40"
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextTestimonial}
                  data-testid="next-testimonial-btn"
                  className="p-4 bg-white/5 border border-white/10 rounded-full hover:bg-[#B87333] hover:border-[#B87333] transition-all"
                >
                  <ChevronRight className="text-white" size={24} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Copper Gradient */}
      <section className="py-28 bg-gradient-to-br from-[#B87333] to-[#8B5A2B] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-white mb-8">
            {t("cta.title1")}<br />{t("cta.title2")}
          </h2>
          <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto">
            {t("cta.description")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-5">
            <Link to="/devis">
              <Button
                data-testid="cta-quote-btn-2"
                className="bg-white text-[#B87333] hover:bg-white/90 px-12 py-8 text-xl font-semibold rounded-full shadow-2xl"
              >
                {t("cta.button_quote")}
                <ArrowRight className="ml-3" size={24} />
              </Button>
            </Link>
            <a href="tel:+32488467522">
              <Button
                data-testid="cta-call-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#B87333] px-10 py-8 text-xl rounded-full"
              >
                <Phone className="mr-2" size={22} />
                {t("cta.button_call")}
              </Button>
            </a>
          </div>
          
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/80">
            {t("cta.features").map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <CheckCircle2 size={20} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section - Light */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FAF9F7]" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              {t("contactPage.areas.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
              {t("contactPage.areas.title")}
            </h2>
          </div>
          
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#B87333] to-[#CD7F32] rounded-full text-white shadow-lg shadow-[#B87333]/20">
              <MapPin size={24} />
              <span className="text-xl font-semibold">Toute la Belgique</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <ImageLightbox
        images={galleryImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default HomePage;
